// LabProvidersTab.tsx — G-33 (Patrick 2026-07-30): per-pharmacy LAB PROVIDERS directory.
// Columns: Lab Name · Contacts · Website/portal · Address · Draw types (CHECKBOXES — what the
// provider offers). Feeds G-34: the enrollment door's lab-provider selector.
import { useEffect, useState, useCallback } from 'react'
import * as api from '../../api/client'

const T = {
  text: 'var(--color-text, #1a1a1a)', soft: 'var(--color-text-soft, #6b7280)',
  border: 'var(--color-border, #e5e7eb)', surface: 'var(--color-surface, #fff)',
  primary: 'var(--color-primary, #185FA5)', okBg: '#E1F5EE', okFg: '#085041',
}
const card: React.CSSProperties = { background: T.surface, border: `0.5px solid ${T.border}`, borderRadius: 12, padding: '14px 16px', marginBottom: 12 }
const th: React.CSSProperties = { textAlign: 'left', padding: '9px 12px', fontWeight: 500, fontSize: 12.5, color: T.soft, borderBottom: `0.5px solid ${T.border}`, whiteSpace: 'nowrap' }
const td: React.CSSProperties = { padding: '9px 12px', borderBottom: `0.5px solid ${T.border}`, fontSize: 13, verticalAlign: 'top' }
const btn: React.CSSProperties = { fontSize: 12.5, padding: '6px 11px', border: `0.5px solid ${T.border}`, borderRadius: 8, background: 'transparent', color: T.text, cursor: 'pointer' }
const btnPrimary: React.CSSProperties = { ...btn, background: T.primary, color: '#fff', border: 'none', fontWeight: 600 }
const fieldLabel: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, color: T.soft, marginBottom: 10 }
const input: React.CSSProperties = { height: 34, padding: '0 10px', border: `0.5px solid ${T.border}`, borderRadius: 8, background: T.surface, color: T.text, fontSize: 13 }

export const DRAW_TYPES: { key: keyof api.LabProvider & string; label: string }[] = [
  { key: 'draw_walk_in', label: 'Lab Walk In' },
  { key: 'draw_scheduled', label: 'Lab Scheduled' },
  { key: 'draw_clinic', label: 'Clinic Draw' },
  { key: 'draw_phlebotomist', label: 'Phlebotomist Draw' },
  { key: 'draw_nurse', label: 'Nurse Draw' },
]

const EMPTY: api.LabProviderBody = { name: '', contacts: '', website: '', address: '',
  draw_walk_in: false, draw_scheduled: false, draw_clinic: false, draw_phlebotomist: false, draw_nurse: false }

export default function LabProvidersTab() {
  const [items, setItems] = useState<api.LabProvider[]>([])
  const [editing, setEditing] = useState<api.LabProvider | 'new' | null>(null)
  const [form, setForm] = useState<api.LabProviderBody>(EMPTY)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const load = useCallback(() => { api.listLabProviders(true).then(d => setItems(d.items)).catch(() => setItems([])) }, [])
  useEffect(() => { load() }, [load])

  const open = (p: api.LabProvider | 'new') => {
    setEditing(p); setErr('')
    setForm(p === 'new' ? EMPTY : { name: p.name, contacts: p.contacts ?? '', website: p.website ?? '', address: p.address ?? '',
      draw_walk_in: p.draw_walk_in, draw_scheduled: p.draw_scheduled, draw_clinic: p.draw_clinic,
      draw_phlebotomist: p.draw_phlebotomist, draw_nurse: p.draw_nurse })
  }
  const save = async () => {
    if (!form.name.trim()) { setErr('Lab name is required.'); return }
    setBusy(true); setErr('')
    try {
      if (editing === 'new') await api.createLabProvider(form)
      else if (editing) await api.updateLabProvider(editing.id, form)
      setEditing(null); load()
    } catch (e) { setErr(e instanceof Error ? e.message : 'Save failed') } finally { setBusy(false) }
  }
  const toggleActive = (p: api.LabProvider) =>
    api.updateLabProvider(p.id, { active: !p.active }).then(load).catch(() => {})

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>Lab providers</div>
          <div style={{ fontSize: 12.5, color: T.soft }}>Directory of labs this pharmacy works with — the enrollment's lab-provider selector reads from here. Draw types reflect what each provider offers.</div>
        </div>
        <button style={btnPrimary} onClick={() => open('new')}>+ Add lab provider</button>
      </div>
      <div style={card}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead><tr>{['Lab Name', 'Contacts', 'Website / portal', 'Address', 'Draw types', 'Status', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {items.length === 0 && <tr><td style={td} colSpan={7}>No lab providers yet — add the first one.</td></tr>}
            {items.map(p => (
              <tr key={p.id} style={{ opacity: p.active ? 1 : .55 }}>
                <td style={{ ...td, fontWeight: 600 }}>{p.name}</td>
                <td style={{ ...td, whiteSpace: 'pre-wrap', maxWidth: 200 }}>{p.contacts || '—'}</td>
                <td style={td}>{p.website ? <a href={p.website.startsWith('http') ? p.website : `https://${p.website}`} target="_blank" rel="noreferrer">{p.website}</a> : '—'}</td>
                <td style={{ ...td, maxWidth: 220 }}>{p.address || '—'}</td>
                <td style={td}>{DRAW_TYPES.filter(dt => (p as any)[dt.key]).map(dt => (
                  <span key={dt.key} style={{ fontSize: 11, padding: '2px 9px', borderRadius: 20, background: T.okBg, color: T.okFg, marginRight: 4, whiteSpace: 'nowrap', display: 'inline-block', marginBottom: 2 }}>{dt.label}</span>)) || '—'}
                  {!DRAW_TYPES.some(dt => (p as any)[dt.key]) && '—'}</td>
                <td style={td}>{p.active ? 'Active' : 'Inactive'}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  <button style={btn} onClick={() => open(p)}>Edit</button>{' '}
                  <button style={btn} onClick={() => toggleActive(p)}>{p.active ? 'Deactivate' : 'Reactivate'}</button>
                </td>
              </tr>))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div style={{ ...card, border: `1px solid ${T.primary}` }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>{editing === 'new' ? 'Add lab provider' : `Edit — ${(editing as api.LabProvider).name}`}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 14 }}>
            <label style={fieldLabel}>Lab Name *<input style={input} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></label>
            <label style={fieldLabel}>Website / portal<input style={input} value={form.website ?? ''} onChange={e => setForm({ ...form, website: e.target.value })} placeholder="labcorp.com or portal URL" /></label>
            <label style={fieldLabel}>Contacts<textarea rows={2} style={{ ...input, height: 'auto', padding: '8px 10px', resize: 'vertical', fontFamily: 'inherit' }} value={form.contacts ?? ''} onChange={e => setForm({ ...form, contacts: e.target.value })} placeholder={'phone / fax / email / contact person'} /></label>
            <label style={fieldLabel}>Address<textarea rows={2} style={{ ...input, height: 'auto', padding: '8px 10px', resize: 'vertical', fontFamily: 'inherit' }} value={form.address ?? ''} onChange={e => setForm({ ...form, address: e.target.value })} /></label>
          </div>
          <div style={{ fontSize: 12.5, color: T.soft, marginBottom: 6 }}>Draw types offered</div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
            {DRAW_TYPES.map(dt => (
              <label key={dt.key} style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
                <input type="checkbox" checked={!!(form as any)[dt.key]} onChange={e => setForm({ ...form, [dt.key]: e.target.checked })} />
                {dt.label}
              </label>))}
          </div>
          {err && <div style={{ fontSize: 12.5, color: '#b91c1c', marginBottom: 8 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={btnPrimary} disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
            <button style={btn} onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>)}
    </div>
  )
}
